import { push } from 'connected-react-router';
import { Dispatch } from 'redux';

import * as LoginActions from '../actions/login';
import * as LoginSsoActions from '../actions/loginSso';
import * as MessageActions from '../actions/message';
import * as RootActions from '../actions/root';
import * as SessionActions from '../actions/session';
import i18n from '../resources/i18n';
import Config from '../site.config.json';
import { fetchCMData } from '../utils/CMData';
import { fetchSystemMst, MasterItemCode } from '../utils/MasterData';
import ReplaceStyleKeywords from '../utils/ReplaceStyleKeywords';
import { Routes, Screens } from '../utils/Routes';
import Transfers from '../utils/Transfers';

// short cut
const showProgress = () =>
	LoginActions.showLoginProgress({
		isShowProgress: true,
	});
const hideProgress = () =>
	LoginActions.showLoginProgress({
		isShowProgress: false,
	});

export const fetchMessages = (
	dispatch: Dispatch,
	companyCode: string | undefined
) => {
	Transfers.fetchManagerNoticeMessages(companyCode)
		.then(data => {
			// お知らせの画面表示
			const carouselChangeInterval = +data.carouselChangeInterval;
			const noticeMessages = data.lmcmDisplayManagerNoticeMessages as LoginActions.INoticeMessage[];
			const messages = noticeMessages.map(msgMap => {
				return {
					contact: msgMap.contact,
					message: msgMap.message.map(msg => {
						msg = ReplaceStyleKeywords.replaceStyleKeywords(msg);
						return msg;
					}),
					startDate: msgMap.startDate,
					titleList: msgMap.titleList.map(msg => {
						msg = ReplaceStyleKeywords.replaceStyleKeywords(msg);
						return msg;
					}),
				};
			});
			dispatch(
				LoginActions.setLoginManagerNotice({
					carouselChangeInterval,
					messages,
					pwr: {
						identificationItems: [],
						loginIdLinkEnabled: false,
						passwordLinkEnabled: false,
					},
				})
			);
		})
		.catch(() => {
			// handle network error
		});
};

export const loginSso = (dispatch: Dispatch, cnt: number) => {
	dispatch(showProgress());
	Transfers.loginSso()
		.then(data => {
			if (data.result !== 'success') {
				dispatch(hideProgress());
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content: data.messageKey,
							},
						],
						title: data.titleKey,
					})
				);
				dispatch(
					LoginSsoActions.setAccessURL({
						accessURL: data.accessURL,
					})
				);
			} else {
				if (data.userInfo.needChangePassword) {
					// パスワード変更画面に遷移
					dispatch(hideProgress());
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content: data.messageKey,
								},
							],
							title: data.titleKey,
						})
					);
					const to = Routes.getPath(Screens.CHANGE_PASSWORD);
					const pushState = {
						companyCode: data.userInfo.companyCode,
						loginID: data.userInfo.loginID,
						managerChk: data.userInfo.managerChk,
					};
					dispatch(push(to, pushState));
				} else {
					// ユーザデータを取得し、お知らせ画面に遷移
					dispatch(hideProgress());
					getUserInfo(
						dispatch,
						data.userInfo.loginID,
						data.userInfo.managerChk
					);
				}
			}
		})
		.catch(e => {
			// handle network error
			dispatch(hideProgress());

			// SSOログインする際に、struts2が原因でシステムエラーが発生する場合がある。
			// struts2のバージョンを上げると解消する可能性があるが、暫定対応として
			// 3回までは再帰的な処理を行い、UI上でのエラー表示を極力回避する（#7526）
			if (cnt <= 3) {
				loginSso(dispatch, cnt + 1);
			} else {
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content: i18n.t('loginSso_Scenario_0001'),
							},
						],
						title: i18n.t('loginSso_Scenario_0002'),
					})
				);
				dispatch(
					LoginSsoActions.setAccessURL({
						accessURL: Routes.getPath(Screens.LOGOUT),
					})
				);
			}
		});
};

export const getUserInfo = (
	dispatch: Dispatch,
	loginID: string,
	managerChk: string
) => {
	// 組織一覧取得
	dispatch(hideProgress());
	Transfers.getUserInfoOrgList()
		.then(data => {
			if (!data || !data.orgList) {
				throw new Error();
			}
			return Transfers.getUserInfoOrgInfo(data.orgList[0].orgId);
		})
		.then(() => {
			return Transfers.getLoginUserInfoModel();
		})
		.then(data => {
			dispatch(SessionActions.setSessionLoginID({ loginID }));
			dispatch(
				SessionActions.setSessionToken({ token: data.secureToken })
			);
			const userAttr: SessionActions.IUserAttr = {
				authorityList: data.authorityList,
				lang: data.langCd,
				locale: data.locale,
				loginType: 'SSO',
				managerChk,
				userFamilyName: data.userFamilyName,
				userId: data.userId,
				userName: data.userName,
			};
			dispatch(SessionActions.setSessionUserAttr({ userAttr }));
			dispatch(hideProgress());
			const m = window.location.search.match(/[&?]redirect_path=([^&]+)/);
			const to = m ? m[1] : Routes.getPath(Screens.PERSONAL_PORTAL);
			dispatch(push(to));

			// マスタデータを取得しLocalStorageに保持
			let MULTI_TENANT_MODE: string | null;
			if (Config.multiTenant) {
				MULTI_TENANT_MODE = '1';
			} else {
				MULTI_TENANT_MODE = '0';
			}
			fetchSystemMst(
				MasterItemCode.CAREER_TYPE_CONTROL,
				MULTI_TENANT_MODE,
				data.secureToken
			);
			fetchSystemMst(
				MasterItemCode.QM_APPLICATION_CHECK_NUMBER,
				MULTI_TENANT_MODE,
				data.secureToken
			);

			fetchSystemMst(
				MasterItemCode.QM_CAREER_NUMBER,
				MULTI_TENANT_MODE,
				data.secureToken
			);
			fetchSystemMst(
				MasterItemCode.USE_JINJI_ZOKUSEI,
				'',
				data.secureToken
			);
			fetchSystemMst(
				MasterItemCode.EM_COOPERATION_MODE,
				MULTI_TENANT_MODE,
				data.secureToken
			);
			// CMオプション 取得ANDスキルマップ の表示制御取得
			fetchCMData(data.secureToken);

			// ログイン後メッセージ取得
			fetchLoggedInMessage(dispatch, data.secureToken);
		})
		.catch(e => {
			// handle network error
			dispatch(hideProgress());
		});
};

export const logout = (dispatch: Dispatch) => {
	dispatch(showProgress());
	fetch(Config.tsqPath + '/tsq-sso/logout.action', {
		credentials: 'include',
		method: 'POST',
	})
		.then(response => {
			dispatch(hideProgress());
			dispatch(RootActions.initialize());
			dispatch(push(Routes.getPath(Screens.LOGIN)));
		})
		.catch(() => {
			dispatch(hideProgress());
		});
};

export const changePassword = (dispatch: Dispatch, password: string) => {
	dispatch(showProgress());
	fetch(Config.tsqPath + '/tsq/ui/auth/changePassword.action', {
		body: JSON.stringify({
			changedPassword: password,
		}),
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
		method: 'POST',
	})
		.then(response => {
			if (!response.ok) {
				throw new Error();
			}
			return response.json();
		})
		.then(response => {
			dispatch(hideProgress());
			const data = response.data;
			if (!data.succeededChangePassword.value) {
				// エラー時
				if (
					!data.passwordChangeResultDialogTitle.value ||
					!data.passwordChangeResultDialogMessage.value
				) {
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content: 'COM_F_0001',
								},
							],
							title: 'COM_D_0001',
						})
					);
				} else {
					dispatch(
						MessageActions.showMessage({
							messages: [
								{
									content:
										data.passwordChangeResultDialogMessage
											.value,
								},
							],
							title: data.passwordChangeResultDialogTitle.value,
						})
					);
				}
			} else {
				// 成功時
				dispatch(
					LoginActions.setPasswordChangeSuccess({
						passwordChangeSuccess: true,
					})
				);
				dispatch(
					MessageActions.showMessage({
						messages: [
							{
								content:
									data.passwordChangeResultDialogMessage
										.value,
							},
						],
						title: data.passwordChangeResultDialogTitle.value,
					})
				);
			}
		})
		.catch(() => {
			dispatch(hideProgress());
		});
};

export const loginAfterChangePassword = (
	dispatch: Dispatch,
	loginID: string,
	managerChk: string
) => {
	// デスクトップ表示アクション実行
	fetch(Config.tsqPath + '/tsq-api/dispDesktop.action', {
		credentials: 'include',
		method: 'POST',
	}).then(response => {
		// ユーザデータを取得し、お知らせ画面に遷移
		getUserInfo(dispatch, loginID, managerChk);
	});
};

/**
 * ログイン後メッセージ取得シナリオ
 * @param dispatch Dispatchオブジェクト
 * @param token トークン
 */
export const fetchLoggedInMessage = (dispatch: Dispatch, token: string) => {
	const modelId = 'tsq_desktop_initModel';
	Transfers.read(modelId, token)
		.then(response => response.json())
		.then(receiveData => {
			if (receiveData && receiveData[modelId]) {
				const data = receiveData.tsq_desktop_initModel;
				dispatch(
					LoginActions.setLoggedInMessage({
						displayContentsLicenseInfoMessage:
							data.ctrlInfo.displayContentsLicenseInfoMessage,
						displayDesktopMessageDialog:
							data.ctrlInfo.displayDesktopMessageDialog,
						displayLastLoginIPAddress:
							data.ctrlInfo.displayLastLoginIPAddress,
						displayLastLoginInfo:
							data.ctrlInfo.displayLastLoginInfo,
						displaySecuritySamePwdWarningMessage:
							data.ctrlInfo.displaySecuritySamePwdWarningMessage,
						lastCreatedFaildContentIds:
							data.dispInfo.lastCreatedFaildContentIds,
						lastLoginFailedDateTime:
							data.dispInfo.lastLoginFailedDateTime,
						lastLoginFailedIPAddress:
							data.dispInfo.lastLoginFailedIPAddress,
						lastLoginSuccessDateTime:
							data.dispInfo.lastLoginSuccessDateTime,
						lastLoginSuccessIPAddress:
							data.dispInfo.lastLoginSuccessIPAddress,
						remainChangePwdExpireDateNum:
							data.dispInfo.remainChangePwdExpireDateNum,
					})
				);
			}
		});
};



// WEBPACK FOOTER //
// ./src/scenario/loginSso.ts